import type { ReactNode } from 'react';
import {
  isTerminalStatus,
  type Task,
  type TaskStatusMeta,
  type WorkspaceMember,
} from '../../lib/task-list';
import { cn } from '../../lib/utils';
import {
  TaskAssigneeAvatar,
  TaskContactChip,
  TaskDueDate,
  TaskPriorityBadge,
  TaskStatusBadge,
} from './task-meta';

export interface TaskRowProps {
  task: Task;
  statuses: TaskStatusMeta[];
  members: WorkspaceMember[];
  onClick: () => void;
  /** Show the workspace-status pill. Off where the surrounding section already says the status. */
  showStatus?: boolean;
  /** Supplementary text under the title — a dashboard's "why this is here" reason, say. */
  secondaryText?: string;
  /** Extra chips, rendered after the due date and before the assignee. */
  metaSlot?: ReactNode;
  /**
   * A trailing control. As with `TaskCard`, the slot's own content owns its event handling —
   * the row is clickable.
   */
  actionSlot?: ReactNode;
  className?: string;
}

/**
 * The compact task form factor: dashboards, side panels, and other dense secondary contexts
 * where a full card is too heavy. Renders the same `task-meta` chips as `TaskCard` at row
 * density, so a task reads the same here as it does on the board — it just takes one line.
 *
 * This exists because every dense surface previously invented its own row (a tone-coloured
 * icon here, a lone priority badge there). Reach for this before writing a new one; if it is
 * genuinely too heavy, compose the chips directly rather than drawing new ones.
 */
export function TaskRow({
  task,
  statuses,
  members,
  onClick,
  showStatus = true,
  secondaryText,
  metaSlot,
  actionSlot,
  className,
}: TaskRowProps) {
  const done = isTerminalStatus(statuses, task.status);
  const member = task.assignedTo ? (members.find((m) => m.id === task.assignedTo) ?? null) : null;

  return (
    <div
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 rounded-lg border border-transparent p-2.5 cursor-pointer transition-colors hover:border-border hover:bg-muted/50',
        done && 'opacity-70',
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'truncate text-[13px] font-medium text-foreground',
            done && 'line-through text-muted-foreground'
          )}
        >
          {task.label}
        </div>
        {secondaryText && (
          <p className="truncate text-[11px] text-muted-foreground">{secondaryText}</p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-x-2">
        {showStatus && <TaskStatusBadge statuses={statuses} statusKey={task.status} />}
        <TaskPriorityBadge priority={task.priority} />
        <TaskContactChip contact={task.contact} className="hidden sm:flex" />
        <TaskDueDate dueAt={task.dueAt} done={done} />
        {metaSlot}
        <TaskAssigneeAvatar member={member} />
        {actionSlot}
      </div>
    </div>
  );
}
